<template>
  <v-dialog v-model="internalOpen" max-width="800" :retain-focus="false">
    <v-card>
      <v-card-title>{{ $t('event_settings.org_dialog.title') }}</v-card-title>
      <v-card-title>{{  props.raceName  }}</v-card-title>

      <v-alert type="error" v-if="errorMessage" class="mx-4">{{ errorMessage }}</v-alert>

      <v-card-text>
        <v-skeleton-loader v-if="loading" type="paragraph, paragraph, paragraph, paragraph, paragraph" />
        <template v-else>
          <v-autocomplete
            v-model="currentRace.director"
            :items="officials"
            :label="$t('event_settings.org_dialog.director')"
            multiple item-value="id" item-title="fullName" density="comfortable"
            :disabled="eventClosed"
          />
          <v-autocomplete
            v-model="currentRace.main_referee"
            :items="officials"
            :label="$t('event_settings.org_dialog.main_referee')"
            multiple item-value="id" item-title="fullName" density="comfortable"
            :disabled="eventClosed"
          />
          <v-autocomplete
            v-model="currentRace.course_setter"
            :items="officials"
            :label="$t('event_settings.org_dialog.course_setter')"
            multiple item-value="id" item-title="fullName" density="comfortable"
            :disabled="eventClosed"
          />
          <v-autocomplete
            v-model="currentRace.other_referee"
            :items="officials"
            :label="$t('event_settings.org_dialog.other_referee')"
            multiple item-value="id" item-title="fullName" density="comfortable"
            :disabled="eventClosed"
          />
          <v-autocomplete
            v-model="currentRace.jury_members"
            :items="officials"
            :label="$t('event_settings.org_dialog.jury_members')"
            multiple item-value="id" item-title="fullName" density="comfortable"
            :disabled="eventClosed"
          />
          <v-autocomplete
            v-model="currentRace.delegate"
            :items="officials"
            :label="$t('event_settings.org_dialog.delegate')"
            item-value="id" item-title="fullName" density="comfortable"
            :disabled="eventClosed"
          />
          <v-autocomplete
            v-model="currentRace.organizers_avg"
            :items="officials"
            :label="$t('event_settings.org_dialog.organizers_avg')"
            multiple item-value="id" item-title="fullName" density="comfortable"
            :disabled="eventClosed"
          />
        </template>
      </v-card-text>

      <v-card-actions>
        <v-btn color="grey" @click="cancel">{{ $t('general.cancel') }}</v-btn>
        <v-spacer />
        <v-btn
          color="green-darken-1"
          @click="saveEvent"
          :loading="saving"
          :prepend-icon="eventClosed ? 'mdi-lock' : undefined"
          :disabled="eventClosed || saving"
        >
          {{ $t('general.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import apiClient from '/api.js'
import { useSnackbarStore } from '@/stores/snackbarStore'

/**
 * Props:
 *  - modelValue (Boolean): ovládá otevření dialogu (v-model z rodiče)
 *  - raceId (Number): ID závodu vybraného v rodiči (z endpointu ?type=all)
 */
const props = defineProps({
  modelValue: { type: Boolean, required: true },
  raceId: { type: [Number, null], default: null },
  raceName: { type: String, required: false }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const snackbar = useSnackbarStore()

const internalOpen = ref(false)
watch(() => props.modelValue, v => { internalOpen.value = v })
watch(internalOpen, v => emit('update:modelValue', v))

const loading = ref(false)
const saving = ref(false)
const errorMessage = ref('')
const eventClosed = ref(false)

const officials = ref([])
/** currentRace drží jen IDčka (vhodné pro v-autocomplete item-value=id) */
const currentRace = ref({
  director: [],
  main_referee: [],
  course_setter: [],
  other_referee: [],
  jury_members: [],
  organizers_avg: [],
  delegate: null
})

/** Načíst data, když se dialog otevře a máme raceId */
const fetchData = async () => {
  if (!props.raceId) return
  loading.value = true
  errorMessage.value = ''
  try {
    const { data } = await apiClient.get(`/race/org/?race_id=${props.raceId}`)

    eventClosed.value = !!data.event_closed
    officials.value = data.officials || []
    currentRace.value = {
      director: data?.race?.director ?? [],
      main_referee: data?.race?.main_referee ?? [],
      course_setter: data?.race?.course_setter ?? [],
      other_referee: data?.race?.other_referee ?? [],
      jury_members: data?.race?.jury_members ?? [],
      organizers_avg: data?.race?.organizers_avg ?? [],
      delegate: data?.race?.delegate ?? null,
    }
  } catch (e) {
    console.error(e)
    errorMessage.value = e?.response?.data?.error || 'Nepodařilo se načíst činovníky.'
  } finally {
    loading.value = false
  }
}

/** Uložit změny */
const saveEvent = async () => {
  if (!props.raceId) return
  saving.value = true
  errorMessage.value = ''
  try {
    await apiClient.patch('/race/org/', {
      race_id: props.raceId,
      ...currentRace.value
    })
    snackbar.success('Činovníci a organizátoři byli úspěšně uloženi.')
    emit('saved')        // rodič si může refreshnout cokoliv
    internalOpen.value = false
  } catch (e) {
    console.error(e)
    snackbar.error(e?.response?.data?.error || 'Uložení se nepodařilo.')
  } finally {
    saving.value = false
  }
}

const cancel = () => { internalOpen.value = false }

/** Triggery načtení: otevře se dialog + máme raceId */
watch(
  () => ({ open: internalOpen.value, id: props.raceId }),
  ({ open, id }) => { if (open && id) fetchData() }
)
</script>
